'use client';
import { DropdownOption } from '@/app/common';
import { useCallback, useEffect, useRef, useState } from 'react';

type Args = {
  onSelect?: (o: DropdownOption) => void;
};

export function useDropdown({ onSelect }: Args = {}) {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<DropdownOption | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  const select = useCallback(
    (o: DropdownOption) => {
      setSelected(o);
      onSelect?.(o);
      setIsOpen(false);
    },
    [onSelect],
  );

  // outside click
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [close]);

  // focus the list when opened
  useEffect(() => {
    if (isOpen) {
      // next microtask avoids race with mount
      queueMicrotask(() => listRef.current?.focus());
    }
  }, [isOpen]);

  return {
    isOpen,
    selected,
    open,
    close,
    toggle,
    select,
    setSelected,
    rootRef,
    listRef,
  };
}
